import type React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { usePermissions } from 'commons/customHooks/useAuth'
import { formatToPTBRNumber, numberToDecimalString } from 'commons/utils/number'
import { toBase64 } from 'commons/utils/object'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import {
  Button,
  CopyButton,
  DSButtonType,
  EmptyContent,
  type HeadItemType,
  Icon,
  LoadingHavaianas,
  Pagination,
  SlimButton,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'components'
import type { ActionPerm } from 'components/Structures/Header/constants'
import { OVTypes, type StatusNdType } from 'constants/enums'
import { URL_CONST } from 'constants/urls'
import { getEDIRowIcon } from 'container/ManageOrders/utils'
import { ModalContentLayout } from 'layouts/ModalContentLayout'
import { useParams } from 'react-router-dom'
import type { OvToCancelData, OvToSendData } from 'store/OVContext/ovContextTypes'
import { getReasonsRefusal } from 'store/OVContext/ovSlice'
import {
  asyncGetSalesOrderOvs,
  SalesOrderOvCancelOperation,
  SalesOrderOvDeleteOperation,
  SalesOrderSapOperation,
  SendSapOperation,
  setOvsList,
} from 'store/SalesOrderContext/salesOrderSlice'
import type { SalesOrderOvItem } from 'store/SalesOrderContext/types/ovsList'
import { useDispatch, useSelector } from 'store/store'
import { theme } from 'styles/theme/hav-theme'

import CancelOvForm, { type CancelOvReason } from './components/CancelOvForm'
import JustifyNDForm, { type JustifyNDFields } from './components/JustifyNDForm'
import OvListMenu from './components/OvListMenu'
import * as S from './styled'

type ActiveActionsStatus = {
  cancel: boolean
  delete: boolean
  return: boolean
  send: boolean
  conflict: boolean
  yrba: boolean
}

type ActionButtonProps = {
  enabled: boolean
  tooltip: React.ReactNode
  button: React.ReactNode
}

const OvsTab = () => {
  const dispatch = useDispatch()
  const { orderNumber } = useParams()
  const {
    draftBody: { tp_orig: orderOrigin },
    ovsListData,
    ovsListData: { OrdensVenda: ovsList, RowsCount: totalItems, ValorTotalOrdensVenda: totalValue },
    isLoadingOvsList: loading,
    isInternationalEdi,
  } = useSelector((state) => state.salesOrder)

  // NOTE: Any change in order of columns or add/remove columns. Should be updated colunsNotNavigate: [8]
  const tableHead: HeadItemType[] = [
    { columnName: 'Status', label: 'Status' },
    { columnName: 'OV', label: 'OV' },
    { columnName: 'OVSAP', label: 'OV SAP' },
    { columnName: 'Fabrica', label: 'Fábrica' },
    { columnName: 'PrevFaturam', label: 'Prev. faturam.' },
    { columnName: 'PrevEntrega', label: 'Prev. entrega' },
    { columnName: 'Volume', label: 'Volume' },
    { columnName: 'Valor', label: 'Valor (R$)' },
    { columnName: '', label: '' },
  ]

  const getOvStatusColor = (statusNumber: number) => {
    // TODO: Refactor this, to improve legibility of status colors
    const pending = [0, 5, 8, 20, 21, 22],
      approval = [9, 10, 15],
      processing = [30, 45, 50],
      sap = [40],
      reserve = [60],
      shipment = [65, 70],
      invoicing = [75],
      invoiced = [80],
      canceled = [90]

    if (pending.includes(statusNumber)) return theme.colors.alert.danger.dark
    if (approval.includes(statusNumber)) return theme.colors.alert.warning.dark
    if (processing.includes(statusNumber)) return theme.colors.alert.info.dark
    if (sap.includes(statusNumber)) return theme.colors.support.second
    if (reserve.includes(statusNumber)) return theme.colors.support.third
    if (shipment.includes(statusNumber)) return theme.colors.support.fourth
    if (invoicing.includes(statusNumber)) return theme.colors.support.first
    if (invoiced.includes(statusNumber)) return theme.colors.alert.success.dark
    if (canceled.includes(statusNumber)) return theme.colors.neutral.light
  }

  const PAGE_SIZE = 15
  const defaultActionsStatus = {
    cancel: false,
    conflict: false,
    delete: false,
    return: false,
    send: false,
    yrba: false,
  }

  useEffect(() => {
    setActiveActions((old) => ({
      ...old,
      yrba: ovsList.some((ov) => ov.TipoOrdemVenda === OVTypes.YRBA),
    }))
  }, [ovsList])

  const [activeActions, setActiveActions] = useState<ActiveActionsStatus>(defaultActionsStatus)
  const [currentPage, setCurrentPage] = useState(1)
  const [numberOfPages, setNumberOfPages] = useState(0)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [ovsToDelete, setOvsToDelete] = useState<string[]>([])
  const [openCancelModal, setOpenCancelModal] = useState(false)
  const [ovsToCancel, setOvsToCancel] = useState<OvToCancelData[]>([])
  const [openSendModal, setOpenSendModal] = useState(false)
  const [ovsToSend, setOvsToSend] = useState<OvToSendData[]>([])

  const { verifyPermission } = usePermissions()
  function shouldDisable(action: ActionPerm) {
    return !verifyPermission(SCREENS.OV, action)
  }

  const hasUnjustifiedND = useMemo(
    () =>
      ovsList
        .filter((item) => item.checked)
        .some((ov) => ov.NegociacaoDiferenciada && !ov.DataJustificativaNegociacao),
    [ovsList],
  )

  const getTableData = () => {
    const tableData = []

    if (ovsList.length) {
      const ovsData = ovsList.map((ov) => {
        const id = ov.NumeroOrdemVenda
        return {
          checked: ov.checked,
          colunsNotNavigate: [8],
          content: [
            <Tag
              background={getOvStatusColor(+ov.StatusNumber)}
              key={`status-${id}`}
              tooltipLabel={ov.StatusDescription}
            >
              {ov.StatusNumber}
            </Tag>,
            <S.OrderNumber key={`number-${id}`}>
              <span>{ov.NumeroOrdemVenda}</span>
              <CopyButton text={ov.NumeroOrdemVenda} />
              {renderNDIcon(ov)}
              {getEDIRowIcon(orderOrigin, undefined, true, isInternationalEdi)}
            </S.OrderNumber>,
            <S.OrderNumberSAP key={`number-ov-sap-${id}`}>
              <span>{ov.NumeroOrdemVendaSap}</span>
              {ov.NumeroOrdemVendaSap && <CopyButton text={ov.NumeroOrdemVendaSap} />}
            </S.OrderNumberSAP>,
            ov.CodigoFabrica,
            ov.DataFaturamentoPrevisto,
            ov.DataEntregaCalculada,
            numberToDecimalString(ov.Volume),
            formatToPTBRNumber(ov.ValorTotalOrdemVenda),
            <OvListMenu
              handleCancel={handleCancelAction}
              handleDelete={handleDeleteAction}
              handleSap={handleSapOperation}
              handleSendSap={handleSendSap}
              key={`menu-${id}`}
              ov={ov}
            />,
          ],
          disableCheckbox: ov.TipoOrdemVenda === OVTypes.YRBA,
          id,
          linkNavigate: `${URL_CONST.MANAGE_OV}/${ov.NumeroOrdemVenda}`,
        }
      })

      tableData.push(...ovsData)
    }

    return tableData
  }

  const handleActions = useCallback(
    (updatedItems: SalesOrderOvItem[]) => {
      const checkedItems = updatedItems.filter((item) => item.checked)

      if (!checkedItems.length) {
        setActiveActions(defaultActionsStatus)
        return
      }

      const isCancelEnabled = checkedItems.every((item) => {
        return +item.StatusNumber >= 0 && +item.StatusNumber <= 8
      })

      const isDeleteEnabled = checkedItems.every((item) => {
        return +item.StatusNumber === 0 && !item.NumeroOrdemVendaSap
      })

      const isReturnEnabled = checkedItems.every((item) => {
        // TODO: Refactor this, to improve legibility of status numbers
        const returnStatus = [9, 10, 15, 20, 21, 22, 40, 45]
        return returnStatus.includes(+item.StatusNumber)
      })

      const isSendEnabled = checkedItems.every((item) => {
        return +item.StatusNumber >= 0 && +item.StatusNumber <= 8
      })

      const isConflict =
        checkedItems.some((item) => {
          return +item.StatusNumber <= 8
        }) &&
        checkedItems.some((item) => {
          return +item.StatusNumber > 8
        })

      setActiveActions((old) => ({
        ...old,
        cancel: isCancelEnabled,
        conflict: isConflict,
        delete: isDeleteEnabled,
        return: isReturnEnabled,
        send: isSendEnabled,
      }))
    },
    [ovsList],
  )

  const setCheckedExclusive = (isChecked: boolean, id: string) => {
    const updatedItems = ovsList.map((item) =>
      id === item.NumeroOrdemVenda ? { ...item, checked: isChecked } : { ...item, checked: false },
    )
    dispatch(setOvsList({ ...ovsListData, OrdensVenda: updatedItems }))
    handleActions(updatedItems)
  }

  const toggleCheckedItem = (isChecked: boolean, id?: string) => {
    if (id) {
      const updatedItems = ovsList.map((item) =>
        id === item.NumeroOrdemVenda ? { ...item, checked: isChecked } : item,
      )
      dispatch(setOvsList({ ...ovsListData, OrdensVenda: updatedItems }))
      handleActions(updatedItems)
    } else {
      const updatedItems = ovsList.map((item) => ({
        ...item,
        checked: isChecked,
      }))

      dispatch(setOvsList({ ...ovsListData, OrdensVenda: updatedItems }))
      handleActions(updatedItems)
    }
  }

  const handleDeleteAction = (item?: SalesOrderOvItem) => {
    const checkedItems = ovsList.filter((item) => item.checked)
    const ovNumbers = item
      ? [item.NumeroOrdemVenda]
      : checkedItems.map((item) => item.NumeroOrdemVenda)

    setOpenDeleteModal(true)
    setOvsToDelete(ovNumbers)
  }

  const dispatchDeleteOperation = () => {
    dispatch(
      SalesOrderOvDeleteOperation(
        ovsToDelete,
        { linhas: PAGE_SIZE, paginas: 0 },
        orderNumber ?? '',
      ),
    )
    toggleCheckedItem(false)
    setOpenDeleteModal(false)
  }

  const handleCancelAction = (item?: SalesOrderOvItem) => {
    const checkedItems = ovsList.filter((item) => item.checked)
    const ovNumbers = item
      ? [{ dataUltimaAlteracao: item.DataAlteracao, numeroOrdem: item.NumeroOrdemVenda }]
      : checkedItems.map((item) => {
          return {
            dataUltimaAlteracao: item.DataAlteracao,
            numeroOrdem: item.NumeroOrdemVenda,
          }
        })

    dispatch(getReasonsRefusal())
    setOpenCancelModal(true)
    setOvsToCancel(ovNumbers)
  }

  const dispatchCancelOperation = (data: CancelOvReason) => {
    const body = {
      codigoMotivo: data.reasonsRefusal,
      justificativa: data.refusalDescription,
      ordensVenda: ovsToCancel,
    }
    dispatch(
      SalesOrderOvCancelOperation(body, { linhas: PAGE_SIZE, paginas: 0 }, orderNumber ?? ''),
    )
    toggleCheckedItem(false)
    setOpenCancelModal(false)
  }

  const handleSapOperation = (operation: string, item?: SalesOrderOvItem) => {
    const checkedItems = ovsList.filter((item) => item.checked)
    const body = item
      ? [{ dataAlteracao: item.DataAlteracao, numeroOrdemVenda: item.NumeroOrdemVenda }]
      : checkedItems.map((item) => ({
          dataAlteracao: item.DataAlteracao,
          numeroOrdemVenda: item.NumeroOrdemVenda,
        }))

    dispatch(
      SalesOrderSapOperation(operation, body, { linhas: PAGE_SIZE, paginas: 0 }, orderNumber ?? ''),
    )
    toggleCheckedItem(false)
  }

  const handleSendSap = (item?: SalesOrderOvItem) => {
    const checkedItems = ovsList.filter((item) => item.checked)
    const ovs = item
      ? [{ DataAlteracao: item.DataAlteracao, NumeroOrdemVenda: item.NumeroOrdemVenda }]
      : checkedItems.map((item) => ({
          DataAlteracao: item.DataAlteracao,
          NumeroOrdemVenda: item.NumeroOrdemVenda,
        }))

    if (item) {
      setCheckedExclusive(true, item.NumeroOrdemVenda)
    }
    setOvsToSend(ovs)
    setOpenSendModal(true)
  }

  const dispatchSendSap = async (data?: JustifyNDFields) => {
    let body = { ListaOvs: ovsToSend }
    if (data) {
      const fileString = (await toBase64(data.file)) as string
      body = {
        ...body,
        ...{
          Anexo: fileString.split(',')[1],
          CodigoMotivoNegociacao: data.codReason,
          JustificativaNegociacaoDiferenciada: data.descRequest,
          NegociacaoDiferenciada: data.descReason,
          NomeAnexo: data.file_name,
        },
      }
    }
    dispatch(SendSapOperation(body, { linhas: PAGE_SIZE, paginas: 0 }, orderNumber ?? ''))
    toggleCheckedItem(false)
    setOpenSendModal(false)
  }

  const NDIcons: Record<StatusNdType, { title: string; color: string }> = {
    approved: {
      color: theme.colors.alert.success.dark,
      title: 'Justificativa aprovada',
    },
    justified: {
      color: theme.colors.neutral.light,
      title: 'Justificado',
    },
    pending: {
      color: theme.colors.alert.danger.dark,
      title: 'Justificativa pendente',
    },
  }

  const renderNDIcon = (ov: SalesOrderOvItem) => {
    const ndStatus = (): StatusNdType => {
      if (ov.NegociacaocaoAprovada) return 'approved'
      if (ov.DataJustificativaNegociacao) return 'justified'
      return 'pending'
    }

    if (!ov.NegociacaoDiferenciada) return <></>

    return (
      <Tooltip title={NDIcons[ndStatus()].title}>
        <span>
          <Icon color={NDIcons[ndStatus()].color} icon="nd" />
        </span>
      </Tooltip>
    )
  }

  useEffect(() => {
    dispatch(
      asyncGetSalesOrderOvs({ linhas: PAGE_SIZE, paginas: currentPage - 1 }, orderNumber ?? ''),
    )
  }, [currentPage])

  useEffect(() => {
    setNumberOfPages(Math.ceil(totalItems / PAGE_SIZE))
  }, [ovsList])

  const ActionButton = ({ enabled, tooltip, button }: ActionButtonProps) => {
    return (
      <>
        {enabled ? (
          button
        ) : (
          <Tooltip arrow placement="top" title={tooltip}>
            <div>{button}</div>
          </Tooltip>
        )}
      </>
    )
  }

  return (
    <S.Wrapper>
      <Typography design="hav-default-03-text">
        Nesta página, você pode visualizar e acessar as ordens de venda geradas a partir deste
        pedido. Também é possível executar algumas ações principais, como envio e retorno do SAP, ou
        cancelamento e exclusão de uma OV.
      </Typography>

      {loading ? (
        <S.LoadingContainer>
          <LoadingHavaianas />
        </S.LoadingContainer>
      ) : (
        <>
          {!ovsList.length ? (
            <EmptyContent
              description={
                <>
                  Ao solicitar os materiais e gerar OVs você poderá visualizá-las aqui.
                  <br />É possível fazer a solicitação através da aba “Em aberto”.
                </>
              }
              title="Sem OVs geradas"
            />
          ) : (
            <S.Container>
              <S.TableWrapper>
                <Table
                  bodyData={getTableData()}
                  customAlign={[
                    { colIndex: 0, textAlign: 'center' },
                    { colIndex: 8, textAlign: 'right' },
                  ]}
                  hasCheckbox
                  headData={tableHead}
                  iconColumns={[0]}
                  rowHeight={32}
                  setChecked={toggleCheckedItem}
                />
              </S.TableWrapper>
              <S.SummaryWrapper>
                <div>
                  {totalItems > PAGE_SIZE && (
                    <Pagination
                      currentPage={currentPage}
                      itemsPerPage={PAGE_SIZE}
                      numberOfItems={totalItems}
                      numberOfPages={numberOfPages}
                      setCurrentPage={setCurrentPage}
                    />
                  )}
                </div>
                <S.SummaryRow>
                  <S.SummaryItem>
                    <Typography design="hav-default-02-text">
                      OVs no pedido{activeActions.yrba}
                    </Typography>
                    <span>{numberToDecimalString(totalItems)}</span>
                  </S.SummaryItem>
                  <S.SummaryItem>
                    <Typography design="hav-default-02-text">Valor em OVs</Typography>
                    <span>{formatToPTBRNumber(totalValue)}</span>
                  </S.SummaryItem>
                </S.SummaryRow>
              </S.SummaryWrapper>
              <S.ActionsRow>
                <ActionButton
                  button={
                    <SlimButton
                      content="Cancelar"
                      disabled={!activeActions.cancel || shouldDisable(ACTIONS_SCREENS.CANCEL)}
                      icon={<Icon icon="cancel" size="15" />}
                      id="btn-cancel"
                      onClick={() => handleCancelAction()}
                      type="button"
                      variant="secondary"
                    />
                  }
                  enabled={activeActions.cancel}
                  tooltip={
                    <>
                      Apenas os status entre 00 e 08
                      <br />
                      podem ser cancelados
                    </>
                  }
                />
                <ActionButton
                  button={
                    <SlimButton
                      content="Excluir"
                      disabled={!activeActions.delete || shouldDisable(ACTIONS_SCREENS.DELETE)}
                      icon={<Icon icon="trash" size="15" />}
                      id="btn-delete"
                      onClick={() => handleDeleteAction()}
                      type="button"
                      variant="secondary"
                    />
                  }
                  enabled={activeActions.delete}
                  tooltip={
                    <>
                      Apenas Ordens de Venda sem o número <br />
                      de OV SAP podem ser excluídas
                    </>
                  }
                />
                <ActionButton
                  button={
                    <SlimButton
                      content="Retornar"
                      disabled={!activeActions.return || shouldDisable(ACTIONS_SCREENS.RETURN)}
                      icon={<Icon icon="undo" size="15" />}
                      id="btn-return"
                      onClick={() => handleSapOperation('retornar')}
                      type="button"
                      variant="secondary"
                    />
                  }
                  enabled={activeActions.return}
                  tooltip={
                    <>
                      Apenas os status 09, 15, 20, 21, 22, <br />
                      40 e 45 podem retornar
                    </>
                  }
                />
                <ActionButton
                  button={
                    <SlimButton
                      content="Enviar"
                      disabled={!activeActions.send || shouldDisable(ACTIONS_SCREENS.SEND)}
                      icon={<Icon icon="send" size="15" />}
                      id="btn-send"
                      onClick={() => handleSendSap()}
                      type="button"
                      variant="secondary"
                    />
                  }
                  enabled={activeActions.send}
                  tooltip={
                    <>
                      {activeActions.conflict ? (
                        <>
                          Sua seleção inclui OVs com status conflitantes. Selecione OVs com status
                          compatíveis para realizar ações em massa.
                        </>
                      ) : (
                        <>
                          {activeActions.yrba ? (
                            <>
                              Não é possível fazer o envio massivo de
                              <br /> OVs do tipo YRBA.
                            </>
                          ) : (
                            <>
                              {activeActions.yrba} Apenas os status entre 00 e 08 podem
                              <br />
                              ser enviados
                            </>
                          )}
                        </>
                      )}
                    </>
                  }
                />
              </S.ActionsRow>
            </S.Container>
          )}
        </>
      )}

      {openDeleteModal && (
        <ModalContentLayout
          closeAction={() => setOpenDeleteModal(false)}
          description={`Esta ação excluirá os itens inclusos ${
            ovsToDelete.length > 1 ? 'nestas OVs' : 'nesta OV'
          } e eles serão removidos do
          seu pedido.`}
          title="Excluir"
        >
          <S.Footer>
            <div>
              <Button
                color="secondary"
                content="Voltar"
                design={DSButtonType.ICON_BUTTON}
                onClick={() => setOpenDeleteModal(false)}
                type="button"
              />
            </div>
            <div>
              <Button
                color="primary"
                content="Excluir"
                design={DSButtonType.PRIMARY_BUTTON}
                onClick={dispatchDeleteOperation}
              />
            </div>
          </S.Footer>
        </ModalContentLayout>
      )}

      {openCancelModal && (
        <ModalContentLayout
          closeAction={() => setOpenDeleteModal(false)}
          description={`Deste modo os itens inclusos ${
            ovsToDelete.length > 1 ? 'nestas Ordens de venda' : 'nesta Ordem de venda'
          } serão removidos do pedido. Deseja cancelar ${
            ovsToDelete.length > 1 ? 'estas OVs' : 'essa OV'
          }?`}
          title="Cancelar"
        >
          <CancelOvForm cancelAction={dispatchCancelOperation} setOpen={setOpenCancelModal} />
        </ModalContentLayout>
      )}

      {openSendModal && (
        <ModalContentLayout
          closeAction={() => setOpenSendModal(false)}
          closeButton
          description="Ao enviar a fábrica dará continuidade à sua Ordem de Venda (OV) e os materiais só poderão ser editados novamente caso solicite o Retorno."
          title="Enviar OVs"
          titleSize={20}
          width={hasUnjustifiedND ? 580 : 430}
        >
          {hasUnjustifiedND ? (
            <JustifyNDForm sendAction={dispatchSendSap} setOpen={setOpenSendModal} />
          ) : (
            <S.Footer>
              <SlimButton
                content="Voltar"
                onClick={() => setOpenSendModal(false)}
                type="button"
                variant="secondary"
              />
              <SlimButton content="Enviar" onClick={() => dispatchSendSap()} variant="primary" />
            </S.Footer>
          )}
        </ModalContentLayout>
      )}
    </S.Wrapper>
  )
}

export default OvsTab
